import { action, observableRef } from "mobx";

/** Where a chord sheet was opened from. It decides what the menu button and next and previous do. */
export type From = { kind: "songs" } | { kind: "setlist"; setlistId: string; index: number };

/** The screen that's showing. */
export type Screen =
    | { name: "songs"; songId?: string }
    | { name: "setlists"; setlistId?: string }
    | { name: "settings"; page?: SettingsPage }
    | { name: "sheet"; chordSheetId: string; from: From }
    | { name: EditScreenName; chordSheetId: string; from: From };

/** The pages of the settings: how the app looks, and everything else. */
export type SettingsPage = "appearance" | "general";

/** The two ways to edit a chord sheet: arranging its chords by touch, or as ChordPro text. */
export type EditScreenName = "arrange" | "edit";

/** Which screen shows. Every change is added to the browser's history, so its back button works too. */
export class NavigationStore {
    /**
     * Screens are only ever replaced, never changed, so MobX doesn't need to look inside them. That keeps them
     * plain objects, which the browser's history can store.
     */
    @observableRef accessor screen: Screen = { name: "songs" };

    constructor() {
        if (typeof window === "undefined") return;

        const saved = (history.state as { screen?: Screen } | null)?.screen;

        if (saved) this.screen = saved;

        history.replaceState({ screen: this.screen }, "");
        window.addEventListener("popstate", (event) => this.restore((event.state as { screen?: Screen } | null)?.screen));
    }

    /** Goes to a screen. Back returns to this one. */
    @action
    go(screen: Screen): void {
        this.screen = screen;

        if (typeof window !== "undefined") history.pushState({ screen }, "");
    }

    /** Shows another screen in place of this one, like the next song in a setlist. */
    @action
    replace(screen: Screen): void {
        this.screen = screen;

        if (typeof window !== "undefined") history.replaceState({ screen }, "");
    }

    @action
    private restore(screen: Screen | undefined): void {
        this.screen = screen ?? { name: "songs" };
    }
}
